import { useState } from 'react';
import Button from '../../../external/components/Button/Button';
import {
  StyledDd,
  StyledDefWrapper,
  StyledDl,
  StyledDt,
} from '../../../external/components/DefLists';
import { TextInput } from '../../../external/components/Input/Input';
import { TokenSymbolInput } from '../../../external/components/Input/TokenSymbol';
import { Skeleton } from '../../../external/components/SkeletonLoading/Skeleton';
import { Tag } from '../../../external/components/Tag/Tag';
import Text from '../../../external/components/Text/Text';
import { NakedStakingPool } from '../../../services/staking/staking.types';
import { ProviderRpcError } from '../../../types/metamask.types';
import { NetworkId } from '../../../types/network.types';
import { ButtonContainer } from '../components/ButtonContainer';
import { BigNumberHelper } from '../helpers/BigNumbeHelper';
import { getNetworkText } from '../helpers/getNetworkText';
import { timeHelpers } from '../helpers/timeHelpers';
import { useNakedStakingPool } from '../hooks/usePool';
import { usePreApproved } from '../hooks/usePreapproved';
import { useStakingActions } from '../hooks/useStakingAction';
import { StakingScreenEnum } from './_Screens';

interface ErrorProps {
  code?: number | string | null | undefined;
  message?: string | undefined;
}

interface Props {
  walletNetworkId: NetworkId;
  stakeAmount: string;
  setStakeAmount: React.Dispatch<React.SetStateAction<string>>;
  setMessage: React.Dispatch<React.SetStateAction<ErrorProps>>;
  setScreen: React.Dispatch<React.SetStateAction<StakingScreenEnum>>;
}

export function StakeScreen({
  walletNetworkId,
  stakeAmount,
  setScreen,
  setMessage,
  setStakeAmount,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const pool = useNakedStakingPool();

  const {
    networkId,
    stakingTokenAddress,
    stakingTokenSymbol,
    stakingContractAddress,
    unstakeData,
    tokenBalance,
    stakedBalance,
    stakingTokenDecimals,
  } = pool as NakedStakingPool;

  const unstakingDurationHumanised = timeHelpers.humaniseTime(unstakeData.unstakingDuration);
  const stakeAmountGreaterThanBalance = BigNumberHelper.isGreaterThan(
    stakeAmount || '0',
    tokenBalance || '0',
  );
  const stakeAmountLessThanOrZero = BigNumberHelper.isLessThanOrEqualTo(stakeAmount || 0, 0);
  const tokenBalancetLessThanOrZero = BigNumberHelper.isLessThanOrEqualTo(tokenBalance || 0, 0);

  const { stake, loading: stakingLoading } = useStakingActions({
    pool,
  });
  const {
    isApproved,
    loading: preApproveLoading,
    approve,
  } = usePreApproved({
    networkId,
    stakingContractAddress,
    walletNetworkId,

    stakingTokenAddress,
  });

  const handleCallStake = async () => {
    setLoading(true);
    try {
      await stake(stakeAmount);
      setLoading(false);
      setScreen(StakingScreenEnum.STAKE_SUCCESS);
    } catch (_error) {
      setLoading(false);
      const error = _error as ProviderRpcError;
      console.log(error, 'greska');
      console.log(error);
      console.log(error.code);
      console.log(error.message);
      if (error.code === 4001) {
        // do nothing if RPC error
        return;
      } else {
        setMessage({ code: error.code, message: error.message ?? '' });
        setScreen(StakingScreenEnum.ERROR);
      }
    }
  };

  const handleApprove = async () => {
    // if (isMounted()) {
    try {
      setApproveLoading(true);
      await approve();
      setApproveLoading(false);
    } catch (error) {
      //handle ERROR @TOFIX

      setApproveLoading(false);
    }
    // }
  };

  const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const regex = new RegExp('^[0-9]*[.,]?[0-9]*$');
    if (regex.test(target.value)) {
      setStakeAmount(target.value);
    }
  };

  return (
    <>
      <Button
        size='small'
        variant='secondary'
        dark={true}
        onClick={() => setScreen(StakingScreenEnum.HOME_SCREEN)}
        disabled={stakingLoading || loading}
      >
        ⬅
      </Button>

      <StyledDl>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Current {stakingTokenSymbol} balance
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              <Text as='span' dark={true}>
                {tokenBalance} {stakingTokenSymbol}
              </Text>
            </>
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Current {stakingTokenSymbol} staked
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              <Text as='span'>
                {BigNumberHelper.formatBigNumb(stakedBalance || 0, stakingTokenDecimals)}{' '}
                {stakingTokenSymbol}
              </Text>
            </>
          </StyledDd>
        </StyledDefWrapper>
      </StyledDl>
      <form>
        <Text dark={true} weight={600} style={{ marginBottom: '0.5rem' }}>
          <label htmlFor='stake-amount'>Stake amount</label>
        </Text>
        <div style={{ display: 'block', position: 'relative' }}>
          <div className='flex align-center justify-space-between mt-half'>
            <TextInput
              darkAlt={true}
              id='stake-amount'
              type='text'
              inputMode='decimal'
              disabled={stakingLoading || preApproveLoading || approveLoading}
              onChange={handleChange}
              value={stakeAmount}
              pattern={'^[0-9]*[.,]?[0-9]*$'}
              placeholder='Enter purchase amount'
              autoComplete='off'
            />
            <TokenSymbolInput dark={true}>{stakingTokenSymbol}</TokenSymbolInput>
          </div>
        </div>
        <div className='flex justify-end' style={{ marginTop: '0.5rem' }}>
          <Tag dark={true} onClick={() => setStakeAmount(tokenBalance || '0')}>
            {' '}
            100%
          </Tag>
          <Tag
            dark={true}
            onClick={() => setStakeAmount(BigNumberHelper.divide(tokenBalance || '0', 4 / 3))}
          >
            {' '}
            75%
          </Tag>
          <Tag
            dark={true}
            onClick={() => setStakeAmount(BigNumberHelper.divide(tokenBalance || '0', 2))}
          >
            {' '}
            50%
          </Tag>
          <Tag
            dark={true}
            onClick={() => setStakeAmount(BigNumberHelper.divide(tokenBalance || '0', 4))}
          >
            {' '}
            25%
          </Tag>
        </div>

        <Text color='lightgrey' size='sm'>
          * Staking will start immediately after clicking "Confirm Staking".
        </Text>
        <Text color='lightgrey' size='sm'>
          * You may unstake your tokens at any time. There is an unstaking period of{' '}
          {unstakingDurationHumanised} you must wait before withdrawing your staked tokens.
        </Text>
        <div className='flex direction-column align-center'>
          {tokenBalancetLessThanOrZero && !loading && (
            <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
              {' '}
              You do not have any {stakingTokenSymbol} to stake
            </Text>
          )}

          {stakeAmountGreaterThanBalance && !loading && (
            <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
              {' '}
              You do not have enough {stakingTokenSymbol}
            </Text>
          )}

          {walletNetworkId !== networkId && (
            <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
              {' '}
              Please connect to the {getNetworkText(networkId)} network
            </Text>
          )}

          {preApproveLoading ? (
            <ButtonContainer>
              <Skeleton width='280px' height='38px' dark={true} />
            </ButtonContainer>
          ) : (
            <>
              {
                <>
                  {!isApproved && (
                    <>
                      <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
                        {' '}
                        In order to participate in the pool, you must first approve the smart
                        contract to spend your assets.
                      </Text>
                    </>
                  )}
                  <ButtonContainer>
                    {!isApproved && (
                      <Button
                        onClick={() => handleApprove()}
                        dark={true}
                        loading={approveLoading}
                        disabled={walletNetworkId !== networkId || approveLoading}
                      >
                        {' '}
                        {approveLoading ? 'Approving...' : 'Preapprove'}
                      </Button>
                    )}
                    <Button
                      onClick={() => handleCallStake()}
                      disabled={
                        tokenBalancetLessThanOrZero ||
                        walletNetworkId !== networkId ||
                        !isApproved ||
                        stakeAmountLessThanOrZero ||
                        stakeAmountGreaterThanBalance ||
                        stakingLoading ||
                        loading
                      }
                      loading={stakingLoading}
                      dark={true}
                    >
                      {stakingLoading || loading ? 'Processing...' : 'Confirm staking'}
                    </Button>
                  </ButtonContainer>
                </>
              }
            </>
          )}
        </div>
      </form>
    </>
  );
}
